import { AfterViewInit, ChangeDetectorRef, Component, forwardRef, Injector, Input, OnDestroy, OnInit, ViewChild } from '@angular/core';
import { ControlValueAccessor, NG_VALUE_ACCESSOR, NgControl, Validators } from '@angular/forms';
import { Dropdown, DropdownChangeEvent } from 'primeng/dropdown';

export interface EditSelectable {
    name: string;
    id: string;
    disabled?: boolean;
}

@Component({
    selector: 'nx-edit-select-input',
    template: `
    <div class="nx-edit-select">
        <label *ngIf="showLabel" for="select">{{label | translate}}<ng-container *ngIf=isRequired()>*</ng-container></label>
        <p-dropdown
            #dropdown
            [options]="_options"
            [(ngModel)]="selected"
            (onChange)="onInputChange($event)"
            optionLabel="name"
            [appendTo]="'body'"
            [virtualScroll]="false"
            [filter]="false"
            [disabled]="isDisabled"
            [panelStyleClass]="'nx-edit-select' + (panelWidhtFull ? ' full-width' : '')"
            [autoDisplayFirst]=false
            [showClear]="showClear"
            [placeholder]="placeholder | translate"
            [filter]="filter"
            filterPlaceholder="Suchen..."
            filterBy="name"
            emptyMessage="Keine Einträge gefunden."
            [resetFilterOnHide]="true"/>
        <small *ngIf="helpText">{{helpText | translate}}</small>
    </div>
  `,
    styles: [`
  `],
    providers: [
        {
            provide: NG_VALUE_ACCESSOR,
            useExisting: forwardRef(() => EditSelectInputComponent),
            multi: true
        }
    ]
})
export class EditSelectInputComponent implements ControlValueAccessor, OnInit, AfterViewInit, OnDestroy {

    @ViewChild('dropdown') pDropdown: Dropdown;
    private mutationObserver: MutationObserver;
    _options: EditSelectable[];

    ngControl: NgControl;
    isDisabled: boolean;
    @Input() panelWidhtFull = false;
    @Input() label: string;
    @Input() placeholder: string;
    @Input() helpText: string;
    @Input() showClear = false;
    @Input() showLabel = true;
    @Input() set options(options :EditSelectable[]){
        this._options = options;
        if(this.selectedId){
            this.selected = this._options.find(option => option.id === this.selectedId);
        }
    }
    @Input() filter = false;

    selected: EditSelectable;
    selectedId: string;

    private onChange: (value: string) => void;
    private onTouched: () => void;

    constructor(
        private _injector: Injector,
        private cdr: ChangeDetectorRef
    ) {
    }

    ngAfterViewInit(): void {
        this.cdr.detectChanges();
        this.observeClassChanges();
    }

    ngOnInit() {
        this.ngControl = this._injector.get(NgControl);
        this.ngControl.valueAccessor = this;
    }

    ngOnDestroy() {
        this.mutationObserver?.disconnect();
      }

    writeValue(id: string): void {
        this.selectedId = id;
        // convert the array of ids to an array of objects by using
        // the available options to feed this into the multiselect
        this.selected = this._options?.find(option => option.id === id);
    }

    registerOnChange(fn: (selected: string) => void): void {
        this.onChange = fn;
    }

    registerOnTouched(fn: () => void): void {
        this.onTouched = fn;
    }

    setDisabledState?(isDisabled: boolean): void {
        this.isDisabled = isDisabled;
    }

    onInputChange(event: DropdownChangeEvent): void {
        this.selected = event.value;
        if (this.onChange) {
            // reduce the selected value to its id
            this.onChange(this.selected ? this.selected?.id : undefined);
        }
    }

    onBlur(): void {
        if (this.onTouched) {
            this.onTouched();
        }
    }

    isRequired(): boolean {
        return this.ngControl?.control?.hasValidator(Validators.required);
    }

    /**
     * Workaround: for whatever reason the dropdown element does not fire the blur event. We work
     * around this by observing the class changes: when the class p-inputwrapper-focus gets removed,
     * we fire the event and inform the form control that the input has been touched.
     */
    observeClassChanges() {
        const targetNode = this.pDropdown?.el?.nativeElement;
        if (!targetNode) {
          // target not found
          return;
        }
        this.mutationObserver = new MutationObserver((mutationsList) => {
          for (const mutation of mutationsList) {
            if (mutation.type === 'attributes' && mutation.attributeName === 'class') {
              const classList = (mutation.target as HTMLElement).classList;
              if (!classList.contains('p-inputwrapper-focus')) {
                this.onBlur();
              }
            }
          }
        });
        this.mutationObserver.observe(targetNode, {
          attributes: true,
          attributeFilter: ['class']
        });
      }

}
